<template>
  <div v-if="canView || canEdit || canDelete" class="special-case-list">
    <GridPanelHeader
      :title="$t('tools.special_cases.title')"
      :grid-ref="$refs.grid"
      :show-add="false"
      :show-filter="true"
      :show-refresh="true"
      :show-export="true"
      @filter="filterVisible = true"
      @refresh="refresh"
    />
    <GridFilter
      :visible="filterVisible"
      :title="$t('common.filter')"
      @apply="applyFilters"
      @reset="resetFilters"
      @close="filterVisible = false"
    >
      <div class="filter-field">
        <label>{{ $t("common.status") }}</label>
        <DxSelectBox :value.sync="filters.status" :data-source="statuses" value-expr="value" display-expr="text" />
      </div>
      <div class="filter-field">
        <label>{{ $t("common.date_from") }}</label>
        <DxDateBox
          :value.sync="filters.date_from"
          type="date"
          display-format="yyyy-MM-dd"
          date-serialization-format="yyyy-MM-dd"
          :show-clear-button="true"
        />
      </div>
      <div class="filter-field">
        <label>{{ $t("common.date_to") }}</label>
        <DxDateBox
          :value.sync="filters.date_to"
          type="date"
          display-format="yyyy-MM-dd"
          date-serialization-format="yyyy-MM-dd"
          :show-clear-button="true"
        />
      </div>
    </GridFilter>
    <DxDataGrid
      ref="grid"
      :data-source="dataSource"
      key-expr="uuid"
      :remote-operations="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      :allow-column-reordering="true"
      :row-alternation-enabled="true"
      :element-attr="{ class: 'smagria-grid' }"
    >
      <DxPaging :page-size="25" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="[25, 50, 100]"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxFilterRow :visible="true" :show-operation-chooser="true" apply-filter="auto" />
      <DxGrouping :auto-expand-all="false" />
      <DxGroupPanel :visible="true" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumn
        data-field="status"
        :caption="$t('common.status')"
        cell-template="status"
        :calculate-cell-value="statusValue"
        :allow-filtering="true"
        :allow-sorting="false"
        :fixed="true"
        :width="130"
      >
        <DxLookup :data-source="statuses" value-expr="value" display-expr="text" />
      </DxColumn>
      <DxColumn
        v-for="column in columns"
        :key="column[0]"
        :data-field="column[0]"
        :caption="$t(column[1])"
        :data-type="column[2] || 'string'"
        :format="column[3]"
        :allow-filtering="true"
        :allow-sorting="false"
        :min-width="120"
      />
      <DxColumn :caption="$t('tools.special_cases.import_id')" data-field="data_import_id" :allow-filtering="true" :allow-sorting="false" />
      <DxColumn
        cell-template="actions"
        :width="70"
        :fixed="true"
        fixed-position="right"
        :allow-filtering="false"
        :show-in-column-chooser="false"
      />
      <template #status="{ data }"
        ><span class="status-label" :class="{ processed: data.data.processed }">{{ statusText(data.data) }}</span></template
      >
      <template #actions="{ data }">
          <DxActionMenu
            :grid-ref="$refs.grid"
            row-key="uuid"
            :row="data.data"
            :items="actionMenuItems(data.data)"
            :merge-with-default-items="false"
            :menu-width="210"
          />
      </template>
    </DxDataGrid>
    <DxPopup
      :visible.sync="infoVisible"
      :title="$t('tools.special_cases.generated')"
      width="95%"
      :max-width="960"
      height="auto"
      :show-close-button="true"
    >
      <DxDataGrid :data-source="generatedWorks" key-expr="uuid" :column-auto-width="true" :show-borders="true">
        <DxColumn data-field="season_on_parcel.parcel.name" :caption="$t('common.parcel')" />
        <DxColumn data-field="net_surface_worked" :caption="$t('tools.import_validator.net_surface_worked')" data-type="number" />
        <DxColumn data-field="work_started_at" :caption="$t('common.work_date')" data-type="datetime" format="yyyy-MM-dd" />
        <DxColumn data-field="work.name" :caption="$t('common.work_name')" />
        <DxColumn data-field="working_time" :caption="$t('tools.import_validator.working_time')" />
        <DxColumn data-field="work_machine.name" :caption="$t('tools.import_validator.work_machine_name')" />
      </DxDataGrid>
    </DxPopup>
  </div>
  <Forbidden v-else />
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapGetters } from "vuex";
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxColumnChooser,
  DxFilterRow,
  DxGrouping,
  DxGroupPanel,
  DxLookup,
} from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import DxSelectBox from "devextreme-vue/select-box";
import DxDateBox from "devextreme-vue/date-box";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import Forbidden from "@/pages/errors/Forbidden.vue";
import { permissions, permissionTypes, canView, canEdit, canDelete } from "@/helpers/Permissions";
import { confirmDialog } from "@/helpers/common";
import { SPECIAL_CASES_ENDPOINT, sourceColumns } from "./fields";

export default {
  name: "ValidatedDataSpecialCases",
  middleware: ["auth"],
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxColumnChooser,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxLookup,
    DxPopup,
    DxSelectBox,
    DxDateBox,
    GridPanelHeader,
    GridFilter,
    DxActionMenu,
    Forbidden,
  },
  data() {
    return {
      columns: sourceColumns,
      filters: this.defaultFilters(),
      appliedFilters: this.defaultFilters(),
      filterVisible: false,
      infoVisible: false,
      generatedWorks: [],
      dataSource: [],
    };
  },
  computed: {
    ...mapGetters({ user: "auth/user" }),
    config() {
      return { permission: permissions.VALIDATED_DATA_SPECIAL_CASES, permission_type: permissionTypes.module };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    statuses() {
      return ["pending", "processed", "deleted", "all"].map((value) => ({ value, text: this.$t("tools.special_cases." + value) }));
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    defaultFilters() {
      return {
        status: "pending",
        date_from: moment().subtract(4, "months").format("YYYY-MM-DD"),
        date_to: moment().format("YYYY-MM-DD"),
      };
    },
    async loadData() {
      const response = await axios.get(SPECIAL_CASES_ENDPOINT, { params: this.appliedFilters });
      this.dataSource = response.data.data;
    },
    async refresh() {
      await this.loadData();
      if (this.$refs.grid) this.$refs.grid.instance.refresh();
    },
    async applyFilters() {
      this.appliedFilters = { ...this.filters };
      this.filterVisible = false;
      await this.loadData();
      if (this.$refs.grid) {
        this.$refs.grid.instance.pageIndex(0);
        this.$refs.grid.instance.refresh();
      }
    },
    resetFilters() {
      this.filters = this.defaultFilters();
      this.applyFilters();
    },
    statusText(row) {
      return this.$t("tools.special_cases." + (row.deleted_at ? "deleted" : row.processed ? "processed" : "pending"));
    },
    statusValue(row) {
      return row.deleted_at ? "deleted" : row.processed ? "processed" : "pending";
    },
    actionMenuItems(row) {
      if (row.processed) {
        return [
          {
            key: "generated",
            label: this.$t("tools.special_cases.generated"),
            icon: require("@/assets/images/icons/details_file.svg"),
            action: () => this.showGenerated(row),
          },
        ];
      }
      if (row.deleted_at) return [];

      const items = [];
      if (this.canEdit) {
        items.push(
          {
            key: "assign",
            label: this.$t("tools.special_cases.assign"),
            icon: require("@/assets/images/icons/edit.svg"),
            action: () => this.edit(row, "assign"),
          },
          {
            key: "serve",
            label: this.$t("tools.special_cases.serve"),
            icon: require("@/assets/images/desktop/icons/controls/link-alt.svg"),
            action: () => this.edit(row, "serve"),
          },
        );
      }
      if (this.canDelete) {
        items.push({
          key: "delete",
          label: this.$t("common.delete"),
          icon: require("@/assets/images/desktop/icons/controls/delete.svg"),
          class: "delete",
          action: () => this.remove(row),
        });
      }
      return items;
    },
    edit(row, mode) {
      this.$router.push({ name: "validated-data-special-case-editor", params: { uuid: row.uuid }, query: { mode } });
    },
    async showGenerated(row) {
      const response = await axios.get(SPECIAL_CASES_ENDPOINT + "/" + row.uuid);
      this.generatedWorks = response.data.data.generated_works;
      this.infoVisible = true;
    },
    async remove(row) {
      try {
        await confirmDialog(this.$t("tools.special_cases.delete_confirm"), this.$t("warning"), {
          okText: this.$t("common.delete"),
          cancelText: this.$t("common.cancel"),
        });
      } catch (error) {
        return;
      }
      await axios.delete(SPECIAL_CASES_ENDPOINT + "/" + row.uuid);
      this.refresh();
    },
  },
};
</script>

<style lang="scss">
.special-case-list .row-actions {
  display: flex;
  gap: 4px;
}
.special-case-list .status-label {
  color: #6f5500;
}
.special-case-list .status-label.processed {
  color: #25754b;
}
</style>
